import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { Placement } from "react-joyride";
import {
  CalendarRange,
  Database,
  Gauge,
  GitCompare,
  Globe,
  LayoutDashboard,
  Library,
  MessageSquareText,
  PencilLine,
  Plug,
  Route,
  ScanText,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

/** The demo's two personas, as its banner toggles them. */
export type TourPersona = "medecin" | "global";

/**
 * Where a stop is told. `patient` is the temporal page of the richest dossier of the demo's own
 * patientèle, resolved when the tour gets there; `anywhere` stays on the current page. The `lab-*`
 * places are the laboratory's worked example — its dataset, its first experiment, the comparison of
 * its first and last experiments — resolved the same way; `lab-corrections` is the corrections journal.
 */
export type TourPlace =
  | "anywhere"
  | "dashboard"
  | "patient"
  | "catalogue"
  | "lab-dataset"
  | "lab-experiment"
  | "lab-corrections"
  | "lab-compare";

export type TourChapterId =
  | "dossier"
  | "patientele"
  | "partage"
  | "laboratoire";

export interface TourChapter {
  id: TourChapterId;
  title: MessageDescriptor;
  summary: MessageDescriptor;
}

export interface TourStop {
  id: string;
  chapter: TourChapterId;
  icon: LucideIcon;
  title: MessageDescriptor;
  body: MessageDescriptor;
  /** `data-tour` values, tried in order: the first one on screen is the one highlighted. */
  anchors: string[];
  place: TourPlace;
  persona: TourPersona;
  placement: Placement;
  /** A still picture of a gesture the demo account is not allowed to make. */
  preview?: "validation";
}

export const TOUR_CHAPTERS: TourChapter[] = [
  {
    id: "dossier",
    title: msg`Du document au dossier`,
    summary: msg`Sources, lecture des documents, parcours de soins, validation`,
  },
  {
    id: "patientele",
    title: msg`De vos dossiers à votre patientèle`,
    summary: msg`Tableau de bord, cohortes en langage naturel`,
  },
  {
    id: "partage",
    title: msg`Partager sans exposer`,
    summary: msg`Catalogue, comparaison de cohortes, vue globale anonymisée`,
  },
  {
    id: "laboratoire",
    title: msg`Une lecture qui se mesure`,
    summary: msg`Jeu de données validé, évaluation, correctif mesuré`,
  },
];

/**
 * The platform as it works today, in the order a document travels through it. Speciality-agnostic
 * on purpose: the demo is one speciality, the platform is not, and no stop names a field.
 */
export const TOUR_STOPS: TourStop[] = [
  {
    id: "sources",
    chapter: "dossier",
    icon: Plug,
    title: msg`Vos sources, branchées une fois`,
    body: msg`MAGE-X se connecte à votre logiciel métier et accepte aussi vos fichiers. Chaque nuit, les nouveaux documents arrivent d'eux-mêmes : rien à ressaisir, rien à exporter.`,
    anchors: ["sidebar-config"],
    place: "anywhere",
    persona: "medecin",
    placement: "right",
  },
  {
    id: "lecture",
    chapter: "dossier",
    icon: ScanText,
    title: msg`Chaque document est lu, un par un`,
    body: msg`Comptes rendus, courriers, imagerie, questionnaires : l'IA lit chaque document et retient ce qu'il affirme, avec sa date. Chaque moment de la chronologie est daté par le document qui l'atteste ; les pièces sont dans l'onglet « Documents ».`,
    anchors: ["patient-chronology"],
    place: "patient",
    persona: "medecin",
    placement: "left",
  },
  {
    id: "parcours",
    chapter: "dossier",
    icon: Route,
    title: msg`Le dossier devient un parcours de soins`,
    body: msg`Les faits sont regroupés en épisodes : ce qui a motivé la prise en charge, la décision, le geste, le suivi et son issue. Le dossier se lit comme une histoire, et non plus comme une liste de champs.`,
    anchors: ["patient-pathway"],
    place: "patient",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "temps",
    chapter: "dossier",
    icon: CalendarRange,
    title: msg`Le dossier à n'importe quelle date`,
    body: msg`Faites glisser le curseur : le dossier montre ce que l'on savait à cette date. Une valeur n'y apparaît jamais avant le document qui l'a apportée.`,
    anchors: ["patient-timeline"],
    place: "patient",
    persona: "medecin",
    placement: "bottom-start",
  },
  {
    id: "question",
    chapter: "dossier",
    icon: MessageSquareText,
    title: msg`Une question, une réponse sourcée`,
    body: msg`Interrogez le dossier en langage naturel. La réponse cite les passages des documents sur lesquels elle s'appuie : vous la vérifiez d'un coup d'œil.`,
    anchors: ["patient-ask"],
    place: "patient",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "validation",
    chapter: "dossier",
    icon: ShieldCheck,
    title: msg`Le médecin a le dernier mot`,
    body: msg`Chaque valeur se valide ou se corrige d'un geste, et un épisode se revoit en une fois. Une valeur validée fait foi : aucune relecture automatique ne l'écrase. Dans la démo, le dossier reste en lecture seule.`,
    // The open episode's first value: the one a validator's two gestures would sit beside.
    anchors: ["patient-value", "patient-episode"],
    place: "patient",
    persona: "medecin",
    placement: "bottom",
    preview: "validation",
  },
  {
    id: "tableau",
    chapter: "patientele",
    icon: LayoutDashboard,
    title: msg`Votre patientèle, question par question`,
    body: msg`Tous vos parcours se lisent ensemble. Choisissez une question, le tableau de bord y répond sur l'ensemble de vos dossiers ; questions et indicateurs sont ceux de votre spécialité.`,
    anchors: ["pathway-questions"],
    place: "dashboard",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "cohorte",
    chapter: "patientele",
    icon: Sparkles,
    title: msg`Une cohorte en une phrase`,
    body: msg`Décrivez en langage naturel les patients que vous cherchez : la phrase devient des critères, appliqués à tout le tableau de bord, et le résultat s'enregistre comme cohorte. Essayez « Démo IA ».`,
    anchors: ["nl-search"],
    place: "dashboard",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "catalogue",
    chapter: "partage",
    icon: Library,
    title: msg`Comparer des cohortes`,
    body: msg`Vos cohortes enregistrées rejoignent le catalogue. Sélectionnez-en plusieurs : la page Études compare chaque variable avec le test qui lui convient, corrige pour les tests multiples et exporte le tout dans Excel.`,
    anchors: ["catalogue-compare", "sidebar-catalogue"],
    place: "catalogue",
    persona: "medecin",
    placement: "bottom-end",
  },
  {
    id: "globale",
    chapter: "partage",
    icon: Globe,
    title: msg`Toute la spécialité, sans un nom`,
    body: msg`La vue globale réunit les dossiers de toute la spécialité, anonymisés : aucun nom n'y figure, et un effectif trop petit pour garantir l'anonymat n'est jamais affiché. Le bandeau passe d'une vue à l'autre.`,
    anchors: ["demo-persona"],
    place: "dashboard",
    persona: "global",
    placement: "bottom",
  },
  {
    id: "jeu-de-donnees",
    chapter: "laboratoire",
    icon: Database,
    title: msg`Vos validations font la référence`,
    body: msg`Chaque valeur qu'un médecin valide ou corrige devient une réponse de référence. Un jeu de données en réunit une sélection, un exemple par épisode de soins : c'est contre elles que la lecture des documents est mesurée.`,
    anchors: ["dataset-first-example", "dataset-examples"],
    place: "lab-dataset",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "evaluation",
    chapter: "laboratoire",
    icon: Gauge,
    title: msg`Une évaluation, champ par champ`,
    body: msg`Une expérience relit chaque épisode tel que la plateforme l'a daté, et le compare à la réponse du médecin : précision, rappel et F1 pour chaque champ. Le plus faible remonte en tête, c'est là que la lecture se trompe.`,
    anchors: ["experiment-weakest-field", "experiment-per-field"],
    place: "lab-experiment",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "corrections",
    chapter: "laboratoire",
    icon: PencilLine,
    title: msg`Les corrections disent pourquoi`,
    body: msg`Le journal des corrections montre ce que les médecins reprennent, champ par champ, et la raison qu'ils en donnent. La cause d'une erreur y est écrite par ceux qui l'ont vue.`,
    anchors: ["corrections-top-field", "corrections-fields"],
    place: "lab-corrections",
    persona: "medecin",
    placement: "bottom",
  },
  {
    id: "correctif",
    chapter: "laboratoire",
    icon: GitCompare,
    title: msg`Un correctif, et sa mesure`,
    body: msg`La description du champ fautif est précisée, puis l'expérience est rejouée sur les mêmes épisodes. La comparaison montre le gain champ par champ, et ce que le correctif a pu dégrader ailleurs.`,
    anchors: ["compare-top-field", "compare-per-field"],
    place: "lab-compare",
    persona: "medecin",
    placement: "bottom",
  },
];
